/**
 * Scope-family + audience helpers for the grant pickers. `family` groups the
 * picker into scannable sections (the same families as the assistant's tool
 * chests); `audience` filters each surface to scopes its principal can
 * actually HOLD — the server refuses an app-only scope on a user
 * (requireAppScope), so the picker no longer offers grants that cannot work.
 *
 * The API is the source of truth for both; absent values (older server) fall
 * back to the server's own defaults ('both' / 'platform') so nothing vanishes.
 */
import type { Scope } from './types.js';

export type ScopeAudience = 'user' | 'app' | 'both';

export const audienceOf = (scope: Pick<Scope, 'audience'>): ScopeAudience =>
  scope.audience === 'user' || scope.audience === 'app' ? scope.audience : 'both';

export const familyOf = (scope: Pick<Scope, 'family'>): string => scope.family ?? 'platform';

export interface ScopeFamilyMeta {
  /** Section header ("Unit data"). */
  label: string;
  /** Plain-language framing beside the header. */
  hint?: string;
  order: number;
}

export const SCOPE_FAMILY_META: Record<string, ScopeFamilyMeta> = {
  assistant: { label: 'Assistant', order: 0 },
  unit_data: { label: 'Unit data', order: 1 },
  knowledge: { label: 'Knowledge', order: 2 },
  schedule: { label: 'Schedule', order: 3 },
  people_fleet: { label: 'People & fleet', order: 4 },
  analytics: { label: 'Analytics', hint: 'fleet-wide; grant to leadership', order: 5 },
  platform: { label: 'Platform machinery', hint: 'machine-to-machine', order: 6 },
};

/**
 * Reach badge, DERIVED from the key's suffix — never hand-curated (a manual
 * flag is a maintenance debt; the naming convention is already the tier
 * vocabulary, so a correctly-named scope badges itself forever).
 *   *.ReadAll → "fleet-wide" — every unit/person, aggregate disclosure
 * All other suffixes carry no badge; the description does the talking.
 */
export const reachBadge = (key: string): string | null =>
  key.endsWith('.ReadAll') ? 'fleet-wide' : null;

/** Meta for any family string — operator-created families get a title-cased
 * label and sort after the known ones. */
export const familyMeta = (family: string): ScopeFamilyMeta =>
  SCOPE_FAMILY_META[family] ?? {
    label: family.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
    order: 99,
  };

/** Group scopes by family in section order; empty families dropped. */
export const groupScopesByFamily = (
  scopes: Scope[],
): Array<{ family: string; scopes: Scope[] }> => {
  const byFamily = new Map<string, Scope[]>();
  for (const s of scopes) {
    const fam = familyOf(s);
    byFamily.set(fam, [...(byFamily.get(fam) ?? []), s]);
  }
  return [...byFamily.entries()]
    .map(([family, list]) => ({
      family,
      scopes: list.sort((a, b) => a.key.localeCompare(b.key)),
    }))
    .sort((a, b) => familyMeta(a.family).order - familyMeta(b.family).order);
};
